import { forwardRef } from "react";
import Box from "@mui/material/Box";
import { getColorFromPercentile, getRampOutline } from "./utils";

// The personnel-report .chip treatment, verbatim (personnel-report.css):
// constant #111 bold digits kept legible by four 1px offsets at ZERO blur —
// never a blurred halo (rasterizes as an opaque box in some Chrome
// print-to-PDF builds) and never a white-text flip partway up the scale.
// The shadow sits on the chip so the small numeral inherits it too.
export const CHIP_TEXT_SHADOW =
  "1px 1px 0 rgba(255,255,255,0.4), -1px 1px 0 rgba(255,255,255,0.4), 1px -1px 0 rgba(255,255,255,0.4), -1px -1px 0 rgba(255,255,255,0.4)";

/**
 * The report percentile chip (personnel hybrid): the metric value ON the
 * ramp-colored chip, with the rank/percentile as a small raised numeral
 * beside it (`.chip .pn`). One component so every surface renders the same
 * element — table cells, game log, stat cards.
 *
 * forwardRef + rest-spreading so it can sit directly inside a MUI Tooltip
 * (which clones its child with a ref and event handlers).
 */
const PercentileChip = forwardRef<
  HTMLSpanElement,
  {
    /** The metric value shown on the chip */
    value: React.ReactNode;
    /** Small hybrid numeral (rank ordinal or percentile digits); omit to hide */
    numeral?: React.ReactNode;
    /** 0-100 percentile driving the ramp color (ignored when background set) */
    percentile?: number;
    /** Rank (1 = best) the color comes from, if any: the ramp buckets on it rather than on `percentile` */
    rank?: number;
    rankPopulation?: number;
    /** Explicit background (e.g. a magnitude gradient) */
    background?: string;
    className?: string;
    /** Mirrored to data-fill-pct so fill math stays unit-testable */
    dataFillPct?: number;
    fontSize?: string;
    minWidth?: number;
    /** Size the numeral to its content (long labels like "2nd least") */
    autoNumeralWidth?: boolean;
  } & React.HTMLAttributes<HTMLSpanElement>
>(function PercentileChip(
  {
    value,
    numeral,
    percentile,
    rank,
    rankPopulation = 30,
    background,
    className,
    dataFillPct,
    fontSize = "0.862rem",
    minWidth = 34,
    autoNumeralWidth = false,
    ...rest
  },
  ref
) {
  const colorPercentile = rank !== undefined ? undefined : percentile;
  const outline = background
    ? undefined
    : getRampOutline(colorPercentile, rank, rankPopulation);
  return (
    <Box
      component="span"
      ref={ref}
      className={className}
      data-fill-pct={dataFillPct}
      {...rest}
      sx={{
        // personnel-report.css .chip: inline, tight, 3px radius, NO border.
        display: "inline-block",
        minWidth,
        px: "4px",
        borderRadius: "3px",
        textAlign: "center",
        color: "#111",
        fontWeight: 700,
        fontSize,
        lineHeight: 1.35,
        whiteSpace: "nowrap",
        textShadow: CHIP_TEXT_SHADOW,
        ...(background
          ? { background }
          : {
              backgroundColor: getColorFromPercentile(
                colorPercentile,
                rank,
                rankPopulation
              ),
            }),
        ...(outline ? { boxShadow: outline } : {}),
      }}
    >
      {value}
      {numeral !== undefined && numeral !== null && numeral !== "" && (
        <Box
          component="span"
          className="ChipRankNumeral"
          sx={{
            // .chip .pn — the small hybrid numeral, raised off the baseline.
            // Fixed width (fits "30th" / "100") so chip width doesn't vary
            // with the numeral's digit count and chips align down a column.
            // autoNumeralWidth opts out for long labels ("2nd least" — the
            // Team Profile strip) that would overflow the chip box.
            fontSize: "0.75em",
            fontWeight: 400,
            ml: "2.5px",
            opacity: 0.8,
            position: "relative",
            top: "-1.5px",
            letterSpacing: 0,
            display: "inline-block",
            width: autoNumeralWidth ? "auto" : "2.45em",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {numeral}
        </Box>
      )}
    </Box>
  );
});

export default PercentileChip;
